import SortableList from 'react-sortable-dnd-list'
import { StepCard } from '../StepCard'

import { useToggleStepCardsList } from './hooks/useToggleStepCardsList'

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useToast } from '@/components/ui/use-toast'
import { Skeleton } from '@/components/ui/skeleton'
import type { Step } from '@/services/queries/operation_steps/types'
import { useCallback, useState } from 'react'
import { Button } from '@/components/ui/button'
import { PlusCircleIcon } from '@heroicons/react/24/outline'
import { useCreateOperationStep } from '@/services/queries/operation_steps'
import { useCADPageStore } from '@/state/cad'
import { QUERY_KEYS } from '@/services/queries/document_pages'
import queryClient from '@/queryClient'

export const StepCardList = ({
  documentVersionId,
  documentId,
  steps,
  isReadOnly,
}: {
  documentVersionId: string
  documentId: string
  steps: Step[]
  isReadOnly?: boolean
}) => {
  const toggleStepCardsList = useToggleStepCardsList()

  return (
    <SortableList
      key={steps.length === 1 ? 'single-step' : 'multi-step'}
      className="flex flex-col h-full overflow-y-auto"
      animationDuration={0}
      itemComponent={StepCardItemContainer}
      itemComponentProps={{
        isReadOnly,
        documentId,
        documentVersionId,
        steps,
      }}
      value={steps}
      onChange={async (rawReorderedSteps: Step[]) => {
        await toggleStepCardsList({
          steps: rawReorderedSteps,
          previousSteps: steps,
          documentVersionId,
          isReadOnly: Boolean(isReadOnly),
        })
      }}
    />
  )
}

export const StepCardItemContainer = ({
  dragging,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  dragged,
  children: step,
  isReadOnly,
  documentVersionId,
  steps,
  ...rest
}: {
  dragging: boolean
  dragged: boolean
  documentVersionId: string
  isReadOnly?: boolean
  children: Step
  steps: Step[]
}) => {
  const { toast } = useToast()
  const [isHovering, setIsHovering] = useState(false)
  const { mutate: createOperationStep } = useCreateOperationStep({
    documentVersionId,
    invalidateOnSuccess: false,
  })
  const toggleStepCardsList = useToggleStepCardsList()
  const toggleSingleStep = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      toggleStepCardsList({
        steps: steps,
        previousSteps: steps,
        documentVersionId,
        isReadOnly: Boolean(isReadOnly),
      }).catch(console.error)
    },
    [steps, documentVersionId, isReadOnly, toggleStepCardsList],
  )

  const subAssemblyDocumentPageId = useCADPageStore(
    (state) => state.subAssemblyDocumentPageId || null,
  )
  const onCreateOperation = useCallback(async () => {
    const { documentPage: operation } = await createOperationStep({
      documentVersionId,
      parentDocumentId: subAssemblyDocumentPageId,
      orderNumber: step?.order_number || 1,
    })

    const currentStepIndex = steps.findIndex((s) => s.id === (step as Step).id)

    const updatedSteps = [
      ...steps.slice(0, currentStepIndex),
      operation,
      ...steps.slice(currentStepIndex),
    ]

    await toggleStepCardsList({
      steps: updatedSteps as Step[],
      previousSteps: steps,
      documentVersionId,
      isReadOnly: Boolean(isReadOnly),
    })

    await queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.DOCUMENT_PAGES, { documentVersionId }],
    })

    if (operation) {
      toast({
        title: `New operation added`,
        variant: 'success',
      })
    }
  }, [
    toast,
    createOperationStep,
    documentVersionId,
    subAssemblyDocumentPageId,
    steps,
    toggleStepCardsList,
    isReadOnly,
    step,
  ])

  return (
    <div
      {...rest}
      className={`${dragging ? 'is-dragging' : ''}`}
      // Ensure stable reference to prevent jumping
      {...(step?.id && { id: `sortable-list-item-${step.id}` })}
      // SortableList doesn't handle n-items === 1
      // Workaround: directly toggle in this case
      onMouseDown={steps.length === 1 ? (e) => toggleSingleStep(e) : undefined}
    >
      {step.created ? (
        <>
          <div
            className="min-h-5 w-full flex items-center justify-center"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            <AddOperationButton
              isHovering={isHovering}
              onClick={onCreateOperation}
            />
          </div>
          <StepCard
            stepNumber={step.order_number as number}
            step={step}
            isReadOnly={isReadOnly}
          />
        </>
      ) : (
        <div className="border-gray-300 border rounded-lg p-3 bg-white text-sm cursor-pointer">
          <div className="flex">
            <Skeleton className="h-6 w-20" />
          </div>
          <Skeleton className="p-4 h-64 w-full my-2" />
        </div>
      )}
    </div>
  )
}

type AddOperationButtonProps = {
  isHovering: boolean
  onClick: () => void
}
const AddOperationButton = ({
  isHovering,
  onClick,
}: AddOperationButtonProps) => {
  return isHovering ? (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="py-0.5 flex">
            <Button
              variant="ghost"
              onClick={onClick}
              className="m-0 p-0 h-auto hover:bg-slate-200"
            >
              <PlusCircleIcon className="w-4 h-4 text-slate-500" />
            </Button>
          </div>
        </TooltipTrigger>
        <TooltipContent className="bg-black text-white">
          <p>Add Operation</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : null
}
